import { useLayoutEffect, useMemo, useRef } from "react";
import { useTranslation } from "@coworker/locales";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import CrossIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CrossIconPath";
import EllipsesVerticalPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/EllipsesVerticalPath";
import TaskState from "@coworker/apprestructured/src/task-management/types/TaskState";
import ViewerTop from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/ViewerTop/ViewerTop";
import Texts from "@coworker/apprestructured/src/task-management/components/SheetTop/Texts/Texts";
import Subtitle from "@coworker/apprestructured/src/task-management/components/SheetTop/Subtitle/Subtitle";
import Title from "@coworker/apprestructured/src/task-management/components/SheetTop/Title/Title";
import Badge from "@coworker/apprestructured/src/task-management/components/SheetTop/Badge/Badge";
import {
  calcDaysOverdue,
  calcOverdueString,
  formatDateTime,
} from "@coworker/apprestructured/src/task-management/util";
import { formatDateByLocale } from "@coworker/reusable/formatters/date.formatter";
import {
  getByPath,
  safeGetByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import {
  isLoading,
  Loading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import Priority from "../../../SheetTop/Priority/Priority";
import SafetyAlarm from "../../../SheetTop/SafetyAlarm/SafetyAlarm";
import { useCreateTitle } from "@coworker/apprestructured/src/task-management/services/useCreateTitle";
import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import styles from "./TaskViewerHeader.module.css";
import { useTaskViewerHeaderLayout } from "./useTaskViewerHeaderLayout";

type TaskViewerHeaderProps = {
  task: Record<string, unknown> | Loading;
  onCloseButtonClick: () => void;
  onMenuButtonClick?: () => void;
  recurrenceText: string | null;
  isCompact?: boolean;
  collapseProgress?: number;
  testId?: string | undefined;
};

function TaskViewerHeader({
  task,
  onCloseButtonClick,
  onMenuButtonClick,
  recurrenceText,
  isCompact = false,
  collapseProgress = 0,
  testId,
}: TaskViewerHeaderProps) {
  const headerRef = useTaskViewerHeaderLayout(collapseProgress);
  const contentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const title = useCreateTitle(task);
  const created_at = getTypedValue<string>(getByPath(task, "created_at"));
  const completed_at = getTypedValue<string>(getByPath(task, "completed_at"));
  const closed_at = getTypedValue<string>(getByPath(task, "closed_at"));
  const start = getTypedValue<string | null>(getByPath(task, "start"));
  const state = getTypedValue<TaskState>(getByPath(task, "state"));
  const priority = getTypedValue<string>(getByPath(task, "priority"));
  const safetyAlarm = safeGetByPath(task, "details.safety_alarm") === true;

  const subtitle = useMemo(() => {
    if (
      isLoading(completed_at) ||
      isLoading(created_at) ||
      isLoading(closed_at) ||
      isLoading(start)
    ) {
      return LOADING;
    }
    if (state === "completed") {
      return `${t("completd3String")} ${formatDateTime(completed_at)}`;
    } else if (state === "closed") {
      return `${t("closedFirstUppercaseString")} ${formatDateTime(closed_at)}`;
    } else if (start !== null) {
      return `${t("scheduledForString")} ${formatDateByLocale(start)}`;
    } else {
      return `${t("createdString")} ${formatDateTime(created_at)}`;
    }
  }, [closed_at, completed_at, created_at, start, state, t]);

  const badge = useMemo(() => {
    if (
      isLoading(start) ||
      isLoading(state) ||
      start === null ||
      state !== "pending"
    ) {
      return null;
    }

    const daysOverdue = calcDaysOverdue(start);
    if (daysOverdue > 0) {
      return calcOverdueString(daysOverdue, t);
    } else {
      return null;
    }
  }, [start, state, t]);

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!isCompact || !content?.contains(document.activeElement)) return;

    headerRef.current
      ?.querySelector<HTMLButtonElement>(
        '[data-testid="task-viewer-header-close-button"]'
      )
      ?.focus();
  }, [headerRef, isCompact]);

  return (
    <ViewerTop
      ref={headerRef}
      state={state}
      testId={testId || "viewer-header-top"}
      className={`${styles["header"]} ${isCompact ? styles["compact"] : ""}`}
    >
      <div className={styles["toolbar"]}>
        {isCompact && (
          <FixaText
            className={styles["compactTitle"]}
            data-testid="task-viewer-compact-title"
            textStyle="Heading.XS"
          >
            {isLoading(title) ? <>&nbsp</> : title}
          </FixaText>
        )}
        <div className={styles["actions"]}>
          {isCompact && onMenuButtonClick && (
            <FixaButton
              data-testid="sheet-title-menu-button"
              aria-label={t("menuString")}
              onClick={onMenuButtonClick}
              ssrIcon={EllipsesVerticalPath}
              iconOnly
              type="secondary"
              inverseTheme
              size="small"
            />
          )}
          <FixaButton
            data-testid="task-viewer-header-close-button"
            onClick={onCloseButtonClick}
            ssrIcon={CrossIconPath}
            iconOnly
            type="tertiary"
            inverseTheme
            size="small"
          />
        </div>
      </div>
      <div
        ref={contentRef}
        className={styles["content"]}
        aria-hidden={isCompact}
        inert={isCompact}
      >
        <Texts>
          {badge !== null && <Badge text={badge} />}
          <Title
            light
            onMenuButtonClick={isCompact ? undefined : onMenuButtonClick}
          >
            {isLoading(title) ? <>&nbsp</> : title}
          </Title>
          {!isLoading(priority) && (priority || safetyAlarm) && (
            <div className={styles["badges"]}>
              {priority && <Priority />}
              {safetyAlarm && <SafetyAlarm />}
            </div>
          )}
          <Subtitle light>
            {isLoading(subtitle) ? <>&nbsp</> : subtitle}
          </Subtitle>
          {recurrenceText && <Subtitle light>{recurrenceText}</Subtitle>}
        </Texts>
      </div>
    </ViewerTop>
  );
}

export default TaskViewerHeader;
